/**
 * Panel de Membresía: plan + equipo, self-service (Sprint 25-5, mockup M4
 * ronda 2 — "Panel de Membresía, plan + equipo fusionados").
 *
 * Entrada única en el sidebar ("Membresía", ver `AppShell.tsx`), ruta
 * `/membresia` DENTRO de `ProtectedRoute > OnboardingGate > AppShell`
 * (ver `App.tsx`) — a diferencia de `/membresia/renovar`, esta página
 * requiere tenant activo (no tiene sentido gestionar equipo de una
 * membresía pausada), así que puede vivir detrás del gate sin problema.
 *
 * Dos bloques:
 * - **Plan**: lectura pura de `useMunicipality()` — plan, período, badge
 *   "Activa" (siempre — llegar hasta acá ya implica `tenant_status ===
 *   'active'`, `OnboardingGate` se encarga), vigencia, cupos, y un
 *   upsell discreto a Pro si el tier es esencial. El rol NO cambia este
 *   bloque (ver lede del mockup: "el rol decide cuánta acción hay en el
 *   segundo [bloque]" — el segundo es Equipo, no Plan).
 * - **Equipo**: datos de `fetchEquipo()` (TanStack Query). Admin
 *   (`member_role === 'admin_municipal'`) ve el form de invitar (o la
 *   nota de cupo lleno) + un menú «⋯» por fila no-admin. Cualquier otro
 *   rol ve la misma lista en solo lectura (hallazgo #34 del mockup: la
 *   UI esconde por rol, el backend rechaza por guarda
 *   `require_admin_municipal` — defensa en profundidad, no cosmética).
 *
 * **Dos cosas que este archivo deliberadamente NO inventa** (Ruling
 * Task 10):
 *
 * 1. **Vigencia sin vencimiento registrado.** Desde el issue #734 la fila
 *    dice hasta cuándo está pagado, con `tier_valid_until` del contexto
 *    (el último día que cubre lo pagado, que escribe el backend al
 *    cobrar). A 30 días o menos avisa cuántos quedan (la cláusula 1 del
 *    modelo de negocio notifica con esa anticipación) y al admin le deja
 *    el enlace a renovar. Un municipio activado antes de que existiera el
 *    vencimiento no lo tiene: la fila muestra una raya en vez de deducir
 *    fechas de otra columna.
 * 2. **Cuota del asistente IA.** No existe ningún campo de cuota IA en
 *    `OnboardingContextDTO`. Desde el issue #568 la página sí consulta
 *    `fetchMyPlan()` (para el cupo de expedientes, ver abajo), así que
 *    el dato está a mano — pero la fila "Asistente IA" del mockup sigue
 *    sin implementarse porque nadie la pidió, y el badge del sidebar ya
 *    la comunica.
 *
 * **Cupo de expedientes activos (issue #568):** la fila "Expedientes
 * activos" viene de `useActiveProjectsQuota()`, o sea de
 * `GET /billing/my-plan` — el contexto de onboarding no lo trae. Es la
 * única fila del bloque Plan que depende de una query, y por eso el
 * bloque entero muestra `PlanSkeleton` mientras carga: el founder pidió
 * (7-sep-2026) que ningún bloque se complete por partes, y fue esta
 * misma página la que originó el hallazgo. La query comparte caché con
 * el badge del sidebar (`AssistantQuotaBadge`), que el `AppShell` ya
 * montó, así que en la práctica el skeleton solo se ve entrando por
 * recarga directa a `/membresia`.
 *
 * **"— tú" en la fila del caller (Ruling RT-10, fix ronda 1):**
 * `EquipoMiembroDTO` no trae `user_id`/`is_self` — no hay señal directa
 * del backend de "cuál fila soy yo". `resolverMiFilaId()` resuelve UN
 * SOLO `membership_id` sobre la lista completa (no un booleano por
 * fila) para que a lo sumo una fila quede marcada, incluso con nombres
 * duplicados:
 *
 * - Si el caller es `admin_municipal`: su fila es la fila con
 *   `role === 'admin_municipal'` — invariante de un solo admin por
 *   tenant (RP-6 del backend), exacto y sin ambigüedad. Este mismo
 *   `membership_id` resuelto es el que oculta el kebab en la fila
 *   propia (`MemberRow` compara contra el prop `esTu`, ya no
 *   recalcula "es la fila admin" por su cuenta — antes coincidían
 *   solo porque el caller admin y "la fila admin" son la misma cosa,
 *   ahora es explícito).
 * - Si no: no hay invariante que usar, así que cae al primer miembro
 *   ACEPTADO cuyo `full_name` matchea `context.full_name` (best
 *   effort — dos miembros con el mismo nombre exacto, o un caller
 *   cuyo nombre en el equipo aparece enmascarado, pueden fallar el
 *   match). `.find()` en vez de `.filter()` para que ante nombres
 *   duplicados marque como mucho UNA fila, no todas las que calcen.
 *
 * follow-up BE: exponer `is_self`/`membership_id` del caller en el
 * contexto (`OnboardingContextDTO` o `EquipoDTO`) para que esto deje
 * de necesitar heurísticas del lado del frontend.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent, type ReactNode, type RefObject } from "react";
import { Link } from "react-router-dom";

import { EquipoSkeleton } from "@/components/EquipoSkeleton";
import {
  fetchEquipo,
  invitarMiembro,
  quitarMiembro,
  reenviarInvitacion,
  type EquipoMiembroDTO,
  type EquipoRole,
  type OnboardingContextDTO,
} from "@/lib/api";
import { track } from "@/lib/analytics";
import {
  useActiveProjectsQuota,
  type ActiveProjectsQuota,
} from "@/lib/hooks/useActiveProjectsQuota";
import { diasEntre, fechaDesdeIso, formatoFecha } from "@/lib/membresia-vigencia";
import { useMunicipality } from "@/lib/municipality";

const EQUIPO_QUERY_KEY = ["membresia-equipo"];

const TIER_LABELS: Record<string, string> = {
  esencial: "Esencial",
  pro: "Pro",
};

const PERIODO_LABELS: Record<string, string> = {
  trimestral: "Trimestral",
  semestral: "Semestral",
  annual: "Anual",
  monthly: "Mensual",
};

/**
 * Desde cuántos días antes del vencimiento la fila Vigencia avisa y ofrece
 * renovar: la cláusula 1 del modelo de negocio notifica 30 días antes.
 */
const DIAS_AVISO_VENCIMIENTO = 30;

const ROLE_LABELS: Record<EquipoRole, string> = {
  formulador: "Formulador",
  colaborador: "Colaborador",
};

const ERROR_GENERICO_EQUIPO =
  "No pudimos cargar el equipo. Recarga la página e inténtalo de nuevo.";
const ERROR_GENERICO_INVITAR = "No pudimos enviar la invitación. Inténtalo de nuevo.";
const ERROR_GENERICO_REENVIAR = "No pudimos reenviar la invitación. Inténtalo de nuevo.";
const ERROR_GENERICO_QUITAR = "No pudimos quitar al miembro. Inténtalo de nuevo.";

const SOPORTE_MAILTO = "mailto:soporte@kaptar.com.co";

// --- Helpers de fechas / período -------------------------------------------

/** " · vence en N días" cuando falta poco; nada si el vencimiento está lejos. */
function avisoDeVencimiento(dias: number): string {
  if (dias > DIAS_AVISO_VENCIMIENTO) return "";
  if (dias === 0) return " · vence hoy";
  if (dias === 1) return " · vence mañana";
  return ` · vence en ${dias} días`;
}

/**
 * Fila "Vigencia" (issue #734, ver punto 1 del docstring del módulo).
 * `validUntil` es el último día pagado como `"YYYY-MM-DD"`.
 */
function VigenciaPlan({ validUntil, esAdmin }: { validUntil: string | null; esAdmin: boolean }) {
  if (!validUntil) return <span data-testid="plan-vigencia">—</span>;

  const hasta = fechaDesdeIso(validUntil);
  const dias = diasEntre(new Date(), hasta);
  const urgente = dias <= DIAS_AVISO_VENCIMIENTO;
  const texto =
    dias < 0
      ? `Venció el ${formatoFecha(hasta)}`
      : `Hasta el ${formatoFecha(hasta)}${avisoDeVencimiento(dias)}`;

  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-x-2">
      <span data-testid="plan-vigencia" className={urgente ? "text-amber-700" : undefined}>
        {texto}
      </span>
      {urgente && esAdmin && (
        <Link to="/membresia/renovar" className="text-brand-700 hover:underline">
          Renovar
        </Link>
      )}
    </span>
  );
}

/**
 * Texto de la fila "Expedientes activos".
 *
 * Pro no tiene tope real (el backend le asigna 9999 como centinela), así
 * que decir "12 de 9999 usados" sería absurdo: mostramos el conteo y la
 * promesa del plan. Si la consulta del plan falló, "—" — el mismo
 * fallback que ya usan Vigencia y Cupos del equipo cuando les falta el
 * dato, en vez de un cero que se leería como "no tienes ninguno".
 */
function cupoExpedientesTexto({ quota, isUnlimited }: ActiveProjectsQuota): string {
  if (!quota) return "—";
  if (isUnlimited) return `${quota.used} activos · ilimitados`;
  return `${quota.used} de ${quota.limit} usados`;
}

/** Ver "— tú" (Ruling RT-10) en el docstring del módulo. */
function resolverMiFilaId(
  miembros: EquipoMiembroDTO[],
  context: OnboardingContextDTO | null,
  callerEsAdmin: boolean,
): string | null {
  if (callerEsAdmin) {
    return miembros.find((m) => m.role === "admin_municipal")?.membership_id ?? null;
  }
  if (!context?.full_name) return null;
  return (
    miembros.find((m) => m.accepted_at !== null && m.full_name === context.full_name)
      ?.membership_id ?? null
  );
}

// --- Página ------------------------------------------------------------

export function Membresia() {
  const {
    name,
    displayName,
    tier,
    tierPeriod,
    tierValidUntil,
    memberRole,
    seatsUsed,
    seatsLimit,
    context,
    refetch,
  } = useMunicipality();
  const queryClient = useQueryClient();
  const equipoQuery = useQuery({ queryKey: EQUIPO_QUERY_KEY, queryFn: fetchEquipo });
  const expedientes = useActiveProjectsQuota();
  // A quién quitar + desde qué botón «⋯» se abrió el modal, para
  // devolverle el foco al cerrar (fix final F3 -- ver ConfirmarQuitarModal).
  const [quitarObjetivo, setQuitarObjetivo] = useState<{
    miembro: EquipoMiembroDTO;
    disparador: RefObject<HTMLButtonElement | null>;
  } | null>(null);

  const muni = name || displayName || "tu municipio";
  const esAdmin = memberRole === "admin_municipal";
  const equipo = equipoQuery.data;
  const cupoLleno = Boolean(equipo && equipo.seats_used >= equipo.seats_limit);
  const miFilaId = equipo ? resolverMiFilaId(equipo.miembros, context, esAdmin) : null;

  function invalidarTodo() {
    void queryClient.invalidateQueries({ queryKey: EQUIPO_QUERY_KEY });
    void refetch();
  }

  const periodoLabel = tierPeriod ? (PERIODO_LABELS[tierPeriod] ?? tierPeriod) : null;
  const planTexto =
    (tier ? (TIER_LABELS[tier] ?? tier) : "—") +
    (periodoLabel ? ` · ${periodoLabel.toLowerCase()}` : "");

  return (
    <div className="mx-auto max-w-3xl">
      <header className="mb-6">
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
          Membresía de {muni}
        </h1>
      </header>

      <section className="mb-5 rounded-xl border border-slate-200 bg-white p-5 shadow-xs">
        <h2 className="mb-3 text-xs font-semibold tracking-wide text-slate-400 uppercase">Plan</h2>
        {/* El bloque entero espera al plan: la fila de expedientes depende
            de una query y el resto no, así que pintarlo a medias sería
            justo lo que el founder pidió evitar (7-sep-2026). */}
        {expedientes.isLoading ? (
          <PlanSkeleton />
        ) : (
          <>
            <PlanRow label="Plan">
              <span className="inline-flex flex-wrap items-center justify-end gap-2">
                {planTexto}
                <BadgeActiva />
              </span>
            </PlanRow>
            <PlanRow label="Vigencia">
              <VigenciaPlan validUntil={tierValidUntil} esAdmin={esAdmin} />
            </PlanRow>
            <PlanRow label="Expedientes activos">
              <span data-testid="cupo-expedientes">{cupoExpedientesTexto(expedientes)}</span>
            </PlanRow>
            <PlanRow label="Cupos del equipo">
              {seatsUsed != null && seatsLimit != null
                ? `${seatsUsed} de ${seatsLimit} usados`
                : "—"}
            </PlanRow>
          </>
        )}
        {tier === "esencial" && (
          <p className="mt-3 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-xs leading-relaxed text-brand-800">
            {/* `state.tier`: RenovarMembresia abre con Pro seleccionado en
                vez del plan actual (fix final F8). */}
            <Link
              to="/membresia/renovar"
              state={{ tier: "pro" }}
              className="font-semibold text-brand-700 hover:underline"
            >
              Conoce Pro
            </Link>{" "}
            para sumar el asistente IA y hasta 8 cupos de equipo.
          </p>
        )}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-xs">
        <h2 className="mb-3 text-xs font-semibold tracking-wide text-slate-400 uppercase">
          Equipo
        </h2>

        {/* Skeleton con la forma del contenido real: el Plan ya está pintado
            (viene del contexto) y el Equipo no puede aparecer segundos
            después sin señal (7-sep-2026). */}
        {equipoQuery.isLoading && <EquipoSkeleton conFormulario={esAdmin} />}

        {equipoQuery.isError && (
          <p
            role="alert"
            className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
          >
            {equipoQuery.error instanceof Error ? equipoQuery.error.message : ERROR_GENERICO_EQUIPO}
          </p>
        )}

        {equipo && (
          <>
            {esAdmin &&
              (cupoLleno ? (
                <CupoLlenoNota tier={equipo.tier} seatsLimit={equipo.seats_limit} />
              ) : (
                <InviteForm onInvited={invalidarTodo} />
              ))}

            <ul className="divide-y divide-slate-100">
              {equipo.miembros.map((m) => (
                <MemberRow
                  key={m.membership_id}
                  miembro={m}
                  esAdmin={esAdmin}
                  esTu={m.membership_id === miFilaId}
                  onReenviado={invalidarTodo}
                  onQuitarClick={(miembro, disparador) =>
                    setQuitarObjetivo({ miembro, disparador })
                  }
                />
              ))}
            </ul>
          </>
        )}
      </section>

      {quitarObjetivo && (
        <ConfirmarQuitarModal
          miembro={quitarObjetivo.miembro}
          muni={muni}
          devolverFocoA={quitarObjetivo.disparador}
          onClose={() => setQuitarObjetivo(null)}
          onQuitado={() => {
            setQuitarObjetivo(null);
            invalidarTodo();
          }}
        />
      )}
    </div>
  );
}

// --- Bloque Plan ---------------------------------------------------------

function PlanRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5 text-sm">
      <span className="text-slate-600">{label}</span>
      <span className="text-right font-semibold text-slate-900">{children}</span>
    </div>
  );
}

/**
 * Placeholder del bloque Plan mientras carga `GET /billing/my-plan`.
 * Reserva las cuatro filas del contenido real para que nada salte
 * cuando llega el dato — mismo criterio que `EquipoSkeleton`.
 */
function PlanSkeleton() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid="plan-skeleton"
      className="motion-safe:animate-pulse"
    >
      <span className="sr-only">Cargando el plan…</span>
      <div aria-hidden="true" className="space-y-3 py-1.5">
        {[0, 1, 2, 3].map((fila) => (
          <div key={fila} className="flex items-center justify-between gap-4">
            <div className="h-3.5 w-32 rounded bg-slate-100" />
            <div className="h-3.5 w-28 rounded bg-slate-200" />
          </div>
        ))}
      </div>
    </div>
  );
}

function BadgeActiva() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-cta-100 px-2 py-0.5 text-[10px] font-bold tracking-wide text-cta-700 uppercase">
      <CheckCircleIcon />
      Activa
    </span>
  );
}

// --- Bloque Equipo ---------------------------------------------------------

/**
 * Nota de cupo lleno (reemplaza el form de invitar). Copy por tier --
 * fix ronda 1, hallazgo ALTO: un tenant Pro con 8/8 mostraba "tu plan
 * es Esencial" y un link a "pasar a Pro" que no resuelve nada (Pro ya
 * es el tier tope). `seatsLimit` viene del backend (`equipo.seats_limit`)
 * en vez de un literal -- el "8" en la variante esencial es la ÚNICA
 * excepción: es el cupo del tier Pro (`SEATS_BY_TIER["pro"]` en el
 * backend), que esta pagina no puede leer del tenant actual (no es
 * su propio `seats_limit`) sin inventar un endpoint nuevo -- mismo
 * literal que ya usa `RenovarMembresia.tsx` (`PRO_DIFERENCIAL`).
 */
function CupoLlenoNota({ tier, seatsLimit }: { tier: string; seatsLimit: number }) {
  if (tier === "pro") {
    return (
      <p className="mb-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">
        Tu plan Pro llega hasta {seatsLimit} miembros. Quita a alguien que ya no esté, o escríbenos
        a{" "}
        <a href={SOPORTE_MAILTO} className="font-semibold text-brand-700 hover:underline">
          soporte@kaptar.com.co
        </a>{" "}
        si tu equipo necesita más cupos.
      </p>
    );
  }

  return (
    <p className="mb-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">
      Tu plan Esencial llega hasta {seatsLimit} miembros.{" "}
      <Link
        to="/membresia/renovar"
        state={{ tier: "pro" }}
        className="font-semibold text-brand-700 hover:underline"
      >
        Pasa a Pro
      </Link>{" "}
      para invitar hasta 8, o quita a alguien que ya no esté.
    </p>
  );
}

function InviteForm({ onInvited }: { onInvited: () => void }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<EquipoRole>("formulador");

  const mutation = useMutation({
    mutationFn: invitarMiembro,
    onSuccess: () => {
      setEmail("");
      setRole("formulador");
      onInvited();
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // Evento de intento (fire al enviar, no al confirmar exito) --
    // igual que `municipality_next_actions_cta_clicked`. `role` es el
    // unico dato que viaja; el email tipeado nunca entra a properties.
    track({ event_name: "equipo_invitar_clicked", properties: { role } });
    mutation.mutate({ email, role });
  }

  return (
    <div className="mb-4">
      <form onSubmit={handleSubmit} className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <label className="sr-only" htmlFor="membresia-invitar-email">
          Correo del nuevo miembro
        </label>
        <input
          id="membresia-invitar-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="correo@alcaldia.gov.co"
          className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 focus:outline-hidden"
        />
        <label className="sr-only" htmlFor="membresia-invitar-rol">
          Rol
        </label>
        <select
          id="membresia-invitar-rol"
          value={role}
          onChange={(e) => setRole(e.target.value as EquipoRole)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 focus:outline-hidden"
        >
          <option value="formulador">{ROLE_LABELS.formulador}</option>
          <option value="colaborador">{ROLE_LABELS.colaborador}</option>
        </select>
        <button
          type="submit"
          disabled={mutation.isPending}
          className="shrink-0 rounded-lg bg-cta-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cta-700 focus:ring-2 focus:ring-cta-500 focus:ring-offset-2 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
        >
          {mutation.isPending ? "Invitando…" : "Invitar"}
        </button>
      </form>
      {mutation.isError && (
        <p
          role="alert"
          className="mt-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800"
        >
          {mutation.error instanceof Error ? mutation.error.message : ERROR_GENERICO_INVITAR}
        </p>
      )}
    </div>
  );
}

function ChipAdmin() {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-brand-100 px-2 py-0.5 text-[11px] font-bold text-brand-700">
      <PersonIcon />
      Admin
    </span>
  );
}

function ChipPendiente() {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">
      <ClockIcon />
      Pendiente
    </span>
  );
}

function MemberRow({
  miembro,
  esAdmin,
  esTu,
  onReenviado,
  onQuitarClick,
}: {
  miembro: EquipoMiembroDTO;
  esAdmin: boolean;
  esTu: boolean;
  onReenviado: () => void;
  onQuitarClick: (
    miembro: EquipoMiembroDTO,
    disparador: RefObject<HTMLButtonElement | null>,
  ) => void;
}) {
  // Ref al botón «⋯» de esta fila: el modal de quitar (que vive en el
  // padre) le devuelve el foco al cerrar (fix final F3).
  const kebabRef = useRef<HTMLButtonElement | null>(null);
  const esPendiente = miembro.accepted_at === null;
  // Chip "Admin": por rol de la fila, sin importar quién mira.
  const esFilaAdmin = miembro.role === "admin_municipal";
  // Kebab: unificado con "— tú" (Ruling RT-10) -- antes recalculaba
  // "es la fila admin" por su cuenta, que solo coincidía con "es mi
  // fila" por el invariante de admin único; ahora reusa el mismo
  // resultado que ya resolvió el padre (`resolverMiFilaId`).
  const mostrarKebab = esAdmin && !esTu;

  // Reenviar no cambia nada visible por sí solo: el kebab se cierra antes
  // de disparar la mutation (así que el "Reenviando…" del menú nunca se
  // ve), y `onSuccess` solo invalida -- la lista vuelve idéntica. Sin
  // una señal propia en la fila, el admin no sabe si el correo salió
  // (fix final F2). El estado de la mutation ES la señal: `isPending`
  // y `isSuccess` se pintan abajo como `role="status"`, y se resetea en
  // la siguiente acción de la fila (`mutate()` lo hace solo al
  // reenviar de nuevo; `reset()` explícito al abrir "Quitar…").
  const reenviarMutation = useMutation({
    mutationFn: () => reenviarInvitacion(miembro.membership_id),
    onSuccess: onReenviado,
  });

  return (
    <li className="flex flex-col gap-1 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-slate-900">
            {miembro.full_name}
            {esTu && <span className="font-normal text-slate-500"> — tú</span>}
          </p>
          {miembro.position && (
            <p className="truncate text-xs text-slate-500">{miembro.position}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {esFilaAdmin && <ChipAdmin />}
          {esPendiente && <ChipPendiente />}
          {mostrarKebab && (
            <KebabMenu
              nombre={miembro.full_name}
              botonRef={kebabRef}
              mostrarReenviar={esPendiente}
              reenviarPending={reenviarMutation.isPending}
              onReenviar={() => reenviarMutation.mutate()}
              onQuitar={() => {
                reenviarMutation.reset();
                onQuitarClick(miembro, kebabRef);
              }}
            />
          )}
        </div>
      </div>
      {(reenviarMutation.isPending || reenviarMutation.isSuccess) && (
        <p role="status" className="text-xs text-cta-700">
          {reenviarMutation.isPending
            ? "Reenviando la invitación…"
            : `Invitación reenviada a ${miembro.full_name}`}
        </p>
      )}
      {reenviarMutation.isError && (
        <p role="alert" className="text-xs text-red-700">
          {reenviarMutation.error instanceof Error
            ? reenviarMutation.error.message
            : ERROR_GENERICO_REENVIAR}
        </p>
      )}
    </li>
  );
}

function KebabMenu({
  nombre,
  botonRef,
  mostrarReenviar,
  reenviarPending,
  onReenviar,
  onQuitar,
}: {
  nombre: string;
  /** Ref al botón «⋯» -- lo usa el modal de quitar para devolver el foco. */
  botonRef: RefObject<HTMLButtonElement | null>;
  mostrarReenviar: boolean;
  reenviarPending: boolean;
  onReenviar: () => void;
  onQuitar: () => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={botonRef}
        type="button"
        aria-label={`Acciones para ${nombre}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex h-10 w-10 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
      >
        <KebabIcon />
      </button>
      {open && (
        <div
          role="menu"
          aria-label={`Acciones para ${nombre}`}
          className="absolute right-0 z-20 mt-1 w-56 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        >
          {mostrarReenviar && (
            <button
              type="button"
              role="menuitem"
              disabled={reenviarPending}
              onClick={() => {
                setOpen(false);
                onReenviar();
              }}
              className="flex w-full items-center px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {reenviarPending ? "Reenviando…" : "Reenviar invitación"}
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onQuitar();
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-red-600 hover:bg-red-50"
          >
            <TrashIcon />
            Quitar del equipo…
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Modal de confirmación. Gestión del foco (fix final F3, mismo patrón
 * que `IdeaEntryDialog`): al elegir "Quitar del equipo…" el `menuitem`
 * se desmonta y el foco caía en `body` -- un usuario de teclado o
 * lector de pantalla quedaba "en ninguna parte" con un diálogo modal
 * abierto. Ahora:
 *
 * - Al montar, el foco va a "Cancelar" (la acción segura, no la
 *   destructiva).
 * - Tab / Shift+Tab quedan en loop entre "Cancelar" y "Quitar" -- son
 *   los únicos dos controles del diálogo, así que la trampa es exacta.
 * - Escape cierra (ya estaba).
 * - Al desmontar (Cancelar, Escape, clic afuera o quitar con éxito), el
 *   foco vuelve al botón «⋯» que abrió el modal (`devolverFocoA`, el
 *   ref de la fila).
 */
function ConfirmarQuitarModal({
  miembro,
  muni,
  devolverFocoA,
  onClose,
  onQuitado,
}: {
  miembro: EquipoMiembroDTO;
  muni: string;
  devolverFocoA: RefObject<HTMLElement | null>;
  onClose: () => void;
  onQuitado: () => void;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const cancelarRef = useRef<HTMLButtonElement | null>(null);
  const quitarRef = useRef<HTMLButtonElement | null>(null);

  const mutation = useMutation({
    mutationFn: () => quitarMiembro(miembro.membership_id),
    onSuccess: () => {
      // Fire en onSuccess (no en el clic) -- mismo patron que
      // `admin_staff_revoked`/`admin_tenant_member_revoked`: el evento
      // marca que la remocion se confirmo de verdad, no solo que se
      // intento. Sin properties -- membership_id/nombre son
      // identificables y no aportan nada que `role` sí aporta en el
      // evento gemelo de invitar.
      track({ event_name: "equipo_quitar_confirmado" });
      onQuitado();
    },
  });

  // Foco inicial en "Cancelar" y, al desmontar, de vuelta al «⋯» que
  // abrió el modal. Cleanup en vez de hacerlo en cada callback de
  // cierre: cubre Cancelar, Escape, clic afuera y el éxito de quitar
  // con una sola regla.
  useEffect(() => {
    // Se captura el botón al montar (no en el cleanup): es el «⋯» que
    // abrió ESTE modal, y el ref podría apuntar a otro nodo para cuando
    // el cleanup corra (react-hooks/exhaustive-deps).
    const disparador = devolverFocoA.current;
    cancelarRef.current?.focus();
    return () => {
      disparador?.focus();
    };
  }, [devolverFocoA]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      // Trampa de Tab: el diálogo tiene exactamente dos controles.
      const primero = cancelarRef.current;
      const ultimo = quitarRef.current;
      if (!primero || !ultimo) return;
      const activo = document.activeElement;
      const fueraDelPanel = !panelRef.current?.contains(activo);
      if (e.shiftKey ? activo === primero || fueraDelPanel : activo === ultimo || fueraDelPanel) {
        e.preventDefault();
        (e.shiftKey ? ultimo : primero).focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="quitar-miembro-title"
      className="fixed inset-0 z-100 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="quitar-miembro-title" className="mb-2 text-lg font-bold text-slate-900">
          Quitar del equipo
        </h2>
        <p className="mb-5 text-sm leading-relaxed text-slate-600">
          ¿Quitar a <strong className="font-semibold text-slate-900">{miembro.full_name}</strong> de{" "}
          {muni}? Pierde el acceso de inmediato. Su cupo queda libre.
        </p>
        {mutation.isError && (
          <p
            role="alert"
            className="mb-3 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800"
          >
            {mutation.error instanceof Error ? mutation.error.message : ERROR_GENERICO_QUITAR}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            ref={cancelarRef}
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="rounded-md border border-slate-300 px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:ring-2 focus:ring-slate-400 focus:outline-hidden disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            ref={quitarRef}
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="rounded-md bg-red-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isPending ? "Quitando…" : "Quitar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Iconos (inline, consistentes con TenantPausado.tsx/AppShell.tsx) -----

function CheckCircleIcon() {
  return (
    <svg
      className="h-3 w-3"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.5 12.5l2.5 2.5 4.5-5" />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg
      className="h-3 w-3"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      className="h-3 w-3"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function KebabIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="12" cy="19" r="1.8" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      aria-hidden="true"
    >
      <path d="M3 6h18M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
    </svg>
  );
}
